import DefaultButton from '@/components/common/default/DefaultButton';
import SpiritoryInput from '@/components/common/Forms/SpiritoryInput';
import FilterSection from '@/components/skus/FilterSkus/FilterSection';
import { formatPriceToEuro } from '@/lib/utils/prices.utils';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { CommonEnum } from '@/types/translations/CommonEnum';
import { AlertCircle, Loader2, Euro } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useRange } from 'react-instantsearch';

interface PriceRangeFilterProps {
  attribute: string;
  isOpen: boolean;
  onToggle: () => void;
  icon?: React.ReactNode;
  className?: string;
  secondaryAccentColor?: string;
}

const PRICE_RANGE_DEFAULTS = {
  MIN: 0,
  MAX: 10000,
  STEP: 50,
} as const;

const validatePriceRange = (
  min: number,
  max: number,
  translate: (key: CommonEnum) => string
): { isValid: boolean; error?: string } => {
  if (!isFinite(min) || !isFinite(max) || isNaN(min) || isNaN(max)) {
    return { isValid: false, error: translate(CommonEnum.invalidPriceValues) };
  }
  if (min < 0) return { isValid: false, error: translate(CommonEnum.minimumPriceCannotBeNegative) };
  if (max < 0) return { isValid: false, error: translate(CommonEnum.maximumPriceCannotBeNegative) };
  if (min > max)
    return { isValid: false, error: translate(CommonEnum.minimumPriceCannotExceedMaximumPrice) };
  return { isValid: true };
};

// Enhanced PriceRangeFilter component with better validation and UX
const PriceRangeFilter = memo<PriceRangeFilterProps>(
  ({ attribute, isOpen, onToggle, icon, className = '', secondaryAccentColor }) => {
    const { translate } = useTypedTranslation<CommonEnum>('common');
    const { range, start, refine, canRefine } = useRange({ attribute });

    // Get safe range values, handling infinite/undefined cases
    const safeRangeMin = useMemo(() => {
      const min = range.min;
      return min !== undefined && isFinite(min) && !isNaN(min) ? min : PRICE_RANGE_DEFAULTS.MIN;
    }, [range.min]);

    const safeRangeMax = useMemo(() => {
      const max = range.max;
      return max !== undefined && isFinite(max) && !isNaN(max) ? max : PRICE_RANGE_DEFAULTS.MAX;
    }, [range.max]);

    // UI state is always in cents, but displayed as currency (divide by 100)
    const [localRange, setLocalRange] = useState([safeRangeMin, safeRangeMax]);
    const [isApplying, setIsApplying] = useState(false);
    const [validationError, setValidationError] = useState<string>('');

    // Update local range when external range changes
    useEffect(() => {
      const newMin =
        start[0] !== undefined && isFinite(start[0]) && !isNaN(start[0]) ? start[0] : safeRangeMin;
      const newMax =
        start[1] !== undefined && isFinite(start[1]) && !isNaN(start[1]) ? start[1] : safeRangeMax;
      setLocalRange([newMin, newMax]);
      setValidationError('');
    }, [start, safeRangeMin, safeRangeMax]);

    // index: 0 = min, 1 = max; value is in currency (not cents)
    const handleRangeChange = useCallback(
      (index: number, value: number) => {
        // Convert value from currency to cents
        const valueInCents = Math.round(value * 100);
        const newRange = [...localRange];
        newRange[index] = Math.max(0, valueInCents);

        // Ensure min doesn't exceed max and vice versa
        if (index === 0 && newRange[0] > newRange[1]) {
          newRange[1] = newRange[0];
        } else if (index === 1 && newRange[1] < newRange[0]) {
          newRange[0] = newRange[1];
        }

        setLocalRange(newRange);

        // Validate range
        const validation = validatePriceRange(newRange[0], newRange[1], translate);
        setValidationError(validation.isValid ? '' : validation.error || '');
      },
      [localRange]
    );

    const applyRange = useCallback(async () => {
      if (!canRefine) return;

      const validation = validatePriceRange(localRange[0], localRange[1], translate);
      if (!validation.isValid) {
        setValidationError(validation.error || 'Invalid price range');
        return;
      }

      setIsApplying(true);
      setValidationError('');

      try {
        await new Promise(resolve => setTimeout(resolve, 100));
        refine([localRange[0], localRange[1]]); // send cents
      } catch (error) {
        console.error('Price range refine error:', error);
        setValidationError('Failed to apply price range');
      } finally {
        setIsApplying(false);
      }
    }, [refine, localRange, canRefine]);

    const resetRange = useCallback(() => {
      setLocalRange([safeRangeMin, safeRangeMax]);
      setValidationError('');

      if (canRefine) {
        try {
          refine([safeRangeMin, safeRangeMax]);
        } catch (error) {
          console.error('Price range reset error:', error);
        }
      }
    }, [safeRangeMin, safeRangeMax, refine, canRefine]);

    // Only consider it active if the user has actually changed from the default range
    const hasActiveRange = useMemo(() => {
      // Check if current start values are different from the safe range bounds
      const currentMin =
        start[0] !== undefined && isFinite(start[0]) && !isNaN(start[0]) ? start[0] : safeRangeMin;
      const currentMax =
        start[1] !== undefined && isFinite(start[1]) && !isNaN(start[1]) ? start[1] : safeRangeMax;

      return currentMin !== safeRangeMin || currentMax !== safeRangeMax;
    }, [start, safeRangeMin, safeRangeMax]);

    const isValidRange = useMemo(
      () => validatePriceRange(localRange[0], localRange[1], translate).isValid,
      [localRange]
    );

    // For UI, min/max/step are in currency (not cents)
    const uiMin = safeRangeMin / 100;
    const uiMax = safeRangeMax / 100;
    const uiStep = PRICE_RANGE_DEFAULTS.STEP / 100;

    // If canRefine is false, it means there are no products with price data
    // In this case, we should hide the price filter or show a message
    if (!canRefine) {
      return (
        <FilterSection
          id="price"
          title={translate(CommonEnum.price) || 'Price'}
          icon={<Euro className="tw-h-4 tw-w-4" />}
          isOpen={isOpen}
          onToggle={onToggle}
          hasActiveFilters={false}
          className={className}
        >
          <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-gray-500">
            No products with price data available
          </div>
        </FilterSection>
      );
    }

    return (
      <FilterSection
        id="price"
        title={translate(CommonEnum.price) || 'Price'}
        icon={icon || <Euro className="tw-h-4 tw-w-4" />}
        isOpen={isOpen}
        onToggle={onToggle}
        hasActiveFilters={hasActiveRange}
        isLoading={isApplying}
        className={className}
        secondaryAccentColor={secondaryAccentColor}
      >
        <div className="tw-space-y-2 tw-px-3 tw-py-2">
          <div className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-font-medium tw-text-gray-700">
            <span>{formatPriceToEuro(localRange[0])}</span>
            <span className="tw-text-gray-400">-</span>
            <span>{formatPriceToEuro(localRange[1])}</span>
          </div>

          {validationError && (
            <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-bg-red-50 tw-p-2 tw-text-xs tw-text-red-700">
              <AlertCircle className="tw-h-3 tw-w-3 tw-flex-shrink-0" />
              <span>{validationError}</span>
            </div>
          )}

          <div className="tw-space-y-1">
            <div className="tw-grid tw-grid-cols-2 tw-gap-1">
              <div>
                <SpiritoryInput
                  type="number"
                  min={uiMin}
                  max={uiMax}
                  step={uiStep}
                  value={localRange[0] / 100}
                  onChange={e => handleRangeChange(0, Number(e.target.value))}
                  disabled={!canRefine}
                  className="tw-w-full tw-rounded tw-border tw-border-gray-300 tw-px-2 tw-py-0.5 tw-text-sm disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                  placeholder="Min price"
                  label={translate(CommonEnum.min) || 'Min'}
                />
              </div>
              <div>
                <SpiritoryInput
                  type="number"
                  min={uiMin}
                  max={uiMax}
                  step={uiStep}
                  value={localRange[1] / 100}
                  onChange={e => handleRangeChange(1, Number(e.target.value))}
                  disabled={!canRefine}
                  className="tw-w-full tw-rounded tw-border tw-border-gray-300 tw-px-2 tw-py-0.5 tw-text-sm disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                  placeholder="Max price"
                  label={translate(CommonEnum.max) || 'Max'}
                />
              </div>
            </div>

            {/* Dual Range Slider */}
            <div className="tw-relative tw-my-4 tw-h-8">
              {/* Background track */}
              <div className="tw-absolute tw-left-0 tw-right-0 tw-top-1/2 tw-h-2 tw--translate-y-1/2 tw-transform tw-rounded-lg tw-bg-gray-200" />

              {/* Active range highlight */}
              <div
                className="tw-pointer-events-none tw-absolute tw-top-1/2 tw-h-2 tw--translate-y-1/2 tw-transform tw-rounded-lg tw-bg-blue-500 tw-transition-all tw-duration-150"
                style={{
                  left: `${((localRange[0] - safeRangeMin) / (safeRangeMax - safeRangeMin)) * 100}%`,
                  width: `${((localRange[1] - localRange[0]) / (safeRangeMax - safeRangeMin)) * 100}%`,
                }}
              />

              {/* Min range input */}
              <input
                type="range"
                min={uiMin}
                max={uiMax}
                step={uiStep}
                value={localRange[0] / 100}
                onChange={e => handleRangeChange(0, Number(e.target.value))}
                disabled={!canRefine}
                className="tw-pointer-events-none tw-absolute tw-left-0 tw-top-0 tw-h-8 tw-w-full tw-cursor-pointer tw-appearance-none tw-bg-transparent disabled:tw-cursor-not-allowed disabled:tw-opacity-50 [&::-moz-range-thumb]:tw-h-4 [&::-moz-range-thumb]:tw-w-4 [&::-moz-range-thumb]:tw-cursor-pointer [&::-moz-range-thumb]:tw-appearance-none [&::-moz-range-thumb]:tw-rounded-full [&::-moz-range-thumb]:tw-border-2 [&::-moz-range-thumb]:tw-border-none [&::-moz-range-thumb]:tw-border-white [&::-moz-range-thumb]:tw-bg-blue-600 [&::-moz-range-thumb]:tw-shadow-md disabled:[&::-moz-range-thumb]:tw-bg-gray-400 [&::-webkit-slider-thumb]:tw-pointer-events-auto [&::-webkit-slider-thumb]:tw-h-4 [&::-webkit-slider-thumb]:tw-w-4 [&::-webkit-slider-thumb]:tw-cursor-pointer [&::-webkit-slider-thumb]:tw-appearance-none [&::-webkit-slider-thumb]:tw-rounded-full [&::-webkit-slider-thumb]:tw-border-2 [&::-webkit-slider-thumb]:tw-border-white [&::-webkit-slider-thumb]:tw-bg-blue-600 [&::-webkit-slider-thumb]:tw-shadow-md [&::-webkit-slider-thumb]:tw-transition-colors [&::-webkit-slider-thumb]:hover:tw-bg-blue-700 disabled:[&::-webkit-slider-thumb]:tw-bg-gray-400"
                style={{ zIndex: 1 }}
              />

              {/* Max range input */}
              <input
                type="range"
                min={uiMin}
                max={uiMax}
                step={uiStep}
                value={localRange[1] / 100}
                onChange={e => handleRangeChange(1, Number(e.target.value))}
                disabled={!canRefine}
                className="tw-pointer-events-none tw-absolute tw-left-0 tw-top-0 tw-h-8 tw-w-full tw-cursor-pointer tw-appearance-none tw-bg-transparent disabled:tw-cursor-not-allowed disabled:tw-opacity-50 [&::-moz-range-thumb]:tw-h-4 [&::-moz-range-thumb]:tw-w-4 [&::-moz-range-thumb]:tw-cursor-pointer [&::-moz-range-thumb]:tw-appearance-none [&::-moz-range-thumb]:tw-rounded-full [&::-moz-range-thumb]:tw-border-2 [&::-moz-range-thumb]:tw-border-none [&::-moz-range-thumb]:tw-border-white [&::-moz-range-thumb]:tw-bg-blue-600 [&::-moz-range-thumb]:tw-shadow-md disabled:[&::-moz-range-thumb]:tw-bg-gray-400 [&::-webkit-slider-thumb]:tw-pointer-events-auto [&::-webkit-slider-thumb]:tw-h-4 [&::-webkit-slider-thumb]:tw-w-4 [&::-webkit-slider-thumb]:tw-cursor-pointer [&::-webkit-slider-thumb]:tw-appearance-none [&::-webkit-slider-thumb]:tw-rounded-full [&::-webkit-slider-thumb]:tw-border-2 [&::-webkit-slider-thumb]:tw-border-white [&::-webkit-slider-thumb]:tw-bg-blue-600 [&::-webkit-slider-thumb]:tw-shadow-md [&::-webkit-slider-thumb]:tw-transition-colors [&::-webkit-slider-thumb]:hover:tw-bg-blue-700 disabled:[&::-webkit-slider-thumb]:tw-bg-gray-400"
                style={{ zIndex: 2 }}
              />
            </div>
          </div>
        </div>

        <div className="tw-flex tw-gap-2 tw-px-3 tw-py-2">
          <DefaultButton
            onClick={applyRange}
            disabled={!canRefine || !isValidRange || isApplying}
            className="tw-flex-1 tw-rounded tw-bg-blue-600 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white tw-transition-colors hover:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-1 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
          >
            {isApplying ? (
              <>
                <Loader2 className="tw-mr-1 tw-h-3 tw-w-3 tw-animate-spin" />
                {translate(CommonEnum.applying) || 'Applying...'}
              </>
            ) : (
              translate(CommonEnum.apply) || 'Apply'
            )}
          </DefaultButton>
          {hasActiveRange && (
            <DefaultButton
              onClick={resetRange}
              disabled={!canRefine || isApplying}
              className="tw-rounded tw-border tw-border-gray-300 tw-bg-white tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-gray-700 tw-transition-colors hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-500 focus:tw-ring-offset-1 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
            >
              {translate(CommonEnum.reset) || 'Reset'}
            </DefaultButton>
          )}
        </div>
      </FilterSection>
    );
  }
);

PriceRangeFilter.displayName = 'PriceRangeFilter';

export default PriceRangeFilter;
